import {
  Dispatch,
  InputHTMLAttributes,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useFormState from "utils/useFormState";
import useValidation from "utils/useValidation";
import { isBlank } from "utils/string";
import apiRequest from "utils/apiRequestWithErrorCode";
import { StyledForm } from "components/List/StyledComponents";
import SwitchField from "./Fields/SwitchField";
import {
  CollaborationRole,
  SecurityDefinition,
  SecurityDefinitionRequest,
  // TODO: We cannot import ts files outside of the src/ it's a limitation of CRA
  StorageAccessType,
  StorageProvider,
} from "@dashboard-v3/api";
import { useSnackbar } from "notistack";
import { toStorageType } from "utils/storageProviders";
import {
  canExpireLinks,
  canPreventFileDownload,
  canProtectWithPassword,
  canRemoveDownloadLink,
  canShowAccessOption,
  canShowCollaborationRole,
  collaborationRoles,
  isMxheroStorage,
  providerOptions,
  storageAccessTypes,
} from "./utils";
import SelectField from "components/Forms/SelectField";
import StorageProviderLabel from "components/StorageProviderLabel";
import RetentionConflictModal, {
  getRetentionConflictDetails,
} from "components/RetentionConflictModal";
import { RetentionConflictDetails } from "@dashboard-v3/api";

type Form = {
  access: StorageAccessType;
  canDownload: boolean;
  removeDownloadLink: boolean;
  individualPassword: boolean;
  name: string;
  provider: StorageProvider;
  expiresInDays?: string;
  collaborationRole: CollaborationRole;
};

const initialValues: Form = {
  access: "OPEN",
  canDownload: true,
  removeDownloadLink: false,
  individualPassword: false,
  name: "",
  provider: "" as StorageProvider,
  expiresInDays: "",
  collaborationRole: "VIEWER",
};

interface FormProps {
  refreshList: () => Promise<void>;
  setLoading: Dispatch<SetStateAction<"ALL" | "MORE" | "ITEM">>;
  securityDefinition?: SecurityDefinition;
  onClose: () => void;
}

export default function SecurityDefinitionForm({
  refreshList,
  setLoading,
  onClose: closeForm,
  securityDefinition,
}: FormProps) {
  const { t } = useTranslation("securityDefinitions");
  const { enqueueSnackbar } = useSnackbar();
  const [enableExpiration, setEnableExpiration] = useState(false);
  // Reglas cuyos archivos duran menos que el link de esta definition (MXH-6086)
  const [retentionConflict, setRetentionConflict] =
    useState<RetentionConflictDetails>(null);
  const { formState, reset, setValues, updateValues } =
    useFormState<Form>(initialValues);
  const { errors, validate, clearErrors } = useValidation(formState);

  global.form = formState;

  useEffect(() => {
    if (securityDefinition) {
      const {
        updatedAt,
        createdAt,
        organizationId,
        expiresInDays,
        allowedStorages,
        removeDownloadLink,
        ...definition
      } = securityDefinition;

      const values: Form = {
        ...definition,
        provider: toStorageType(allowedStorages[0]),
        collaborationRole: definition.collaborationRole ?? "VIEWER",
        ...(removeDownloadLink && { removeDownloadLink }),
      };

      if (expiresInDays) {
        values.expiresInDays = expiresInDays.toString();
      }

      setValues(values);
      setEnableExpiration(securityDefinition.expiresInDays > 0);
    }

    return () => {
      clearErrors();
      reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleEnableExpiration() {
    const newState = !enableExpiration;
    setEnableExpiration(newState);

    reset({
      expiresInDays:
        !canExpireLinks(formState.access, formState.provider) || !newState,
    });
  }

  function updateProvider(event) {
    const newProvider = event.target.value;
    updateValues(event);
    resetForm(formState.access, newProvider, formState.canDownload);

    if (!canExpireLinks(formState.access, formState.provider)) {
      setEnableExpiration(false);
    }
  }

  function updateAccess(event) {
    const newAccess = event.target.value;
    resetForm(newAccess, formState.provider, formState.canDownload);
    updateValues(event);
  }

  function toggleCanDownload(event) {
    const { checked } = event.target;
    const removeDownloadLink = formState.removeDownloadLink
      ? false
      : formState.removeDownloadLink;

    setValues({ canDownload: !checked, removeDownloadLink });
  }

  function resetForm(
    access: StorageAccessType,
    storageProvider: StorageProvider,
    canDownload: boolean
  ) {
    reset({
      individualPassword: !canProtectWithPassword(access, storageProvider),
      canDownload: !canPreventFileDownload(access, storageProvider),
      expiresInDays: !canExpireLinks(access, storageProvider),
      access:
        !canShowAccessOption(access, storageProvider) ||
        isMxheroStorage(storageProvider),
      removeDownloadLink: canRemoveDownloadLink(
        access,
        storageProvider,
        canDownload
      ),
      collaborationRole: !canShowCollaborationRole(access, storageProvider),
    });
  }

  const validations = {
    name(value) {
      if (isBlank(value)) {
        return t("form.fields.name.errors.empty");
      }
      return null;
    },
    provider(value) {
      if (!value) {
        return t("form.fields.provider.errors.empty");
      }
      return null;
    },
    expiresInDays(value) {
      if (isBlank(value) && enableExpiration) {
        return t("form.fields.expiresInDays.errors.empty");
      }
      if (parseInt(value, 10) <= 0) {
        return t("form.fields.expiresInDays.errors.negative");
      }
      return null;
    },
  };

  const validateOnBlur = e => {
    const { name } = e.target;
    return validate(name, validations[name]);
  };

  const validateForm = () => {
    return ["name", "provider", "expiresInDays"]
      .map(name => validate(name, validations[name]))
      .every(val => val);
  };

  /**
   * @returns si el formulario puede cerrarse. Solo con un conflicto de
   * retención queda abierto, para corregir y volver a guardar.
   */
  const saveDefinition = async (
    body: SecurityDefinitionRequest,
    id?: string
  ): Promise<boolean> => {
    const path = id ? `/security-definitions/${id}` : "/security-definitions";
    const method = id ? "PUT" : "POST";
    const definition = id ? { ...body, id } : body;
    try {
      setLoading("ALL");
      await apiRequest<SecurityDefinition>(method, path, definition);
      await refreshList();
      return true;
    } catch (e) {
      const conflictDetails = getRetentionConflictDetails(e);

      if (conflictDetails) {
        setRetentionConflict(conflictDetails);
        return false;
      }

      const errorKey = id ? "createError" : "updateError";
      enqueueSnackbar(t(`errors.definitionsRequests.${errorKey}`), {
        variant: "error",
      });
      return true;
    } finally {
      setLoading(null);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      const { expiresInDays, provider, collaborationRole, ...form } = formState;

      const definition: SecurityDefinitionRequest = {
        ...form,
        allowedStorages: [provider],
      };

      if (expiresInDays) {
        const maybeNumber = parseInt(expiresInDays, 10);
        if (!isNaN(maybeNumber)) {
          definition.expiresInDays = maybeNumber;
        }
      }

      if (canShowCollaborationRole(form.access, provider)) {
        definition.collaborationRole = collaborationRole;
      }

      const canClose = await saveDefinition(definition, securityDefinition?.id);
      if (canClose) closeForm();
    }
  };

  return (
    <Dialog open onClose={closeForm} fullWidth>
      <DialogTitle>{t("form.title")}</DialogTitle>
      <DialogContent aria-label="securityDefinitionForm">
        <StyledForm onSubmit={handleSubmit}>
          <TextField
            name="name"
            label={t("form.fields.name.label")}
            variant="outlined"
            fullWidth
            value={formState.name}
            onChange={updateValues}
            error={!!errors.name}
            helperText={errors.name}
            onBlur={validateOnBlur}
            inputProps={{ "data-testid": "sd-name-input" }}
          />
          <FormLabel>
            <Typography variant="subtitle2" color="textSecondary">
              {t("form.fields.storageType.label")}
            </Typography>
          </FormLabel>
          <FormControl variant="outlined" fullWidth>
            <SelectField
              data-testid="sd-storage-select"
              options={providerOptions}
              aria-label="selectProvider"
              name="provider"
              value={formState.provider}
              error={!!errors.provider}
              onChange={updateProvider}
              onBlur={validateOnBlur}
              renderOption={val => (
                <MenuItem key={val} value={val}>
                  <StorageProviderLabel value={val} />
                </MenuItem>
              )}
            />

            <FormHelperText error={!!errors.provider}>
              {errors.provider}
            </FormHelperText>
          </FormControl>

          <FormLabel>
            <Typography variant="subtitle2" color="textSecondary">
              {t("form.fields.access.titile")}
            </Typography>
            {!isMxheroStorage(formState.provider) && (
              <Typography variant="caption" color="textSecondary">
                {t("form.fields.access.label")}
              </Typography>
            )}
          </FormLabel>
          <RadioGroup
            name="access"
            value={formState.access}
            onChange={updateAccess}
          >
            {storageAccessTypes.map(
              item =>
                canShowAccessOption(item, formState.provider) && (
                  <FormControlLabel
                    key={item}
                    label={t(`access.${item}`)}
                    control={
                      <Radio
                        size="small"
                        color="primary"
                        value={item}
                        inputProps={
                          {
                            "aria-label": getAriaLabel(item),
                            "data-testid": `sd-access-${item.toLowerCase()}`,
                          } as InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    }
                  />
                )
            )}
          </RadioGroup>
          {canShowCollaborationRole(formState.access, formState.provider) && (
            <>
              <FormLabel>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("form.fields.collaborationRole.label")}
                </Typography>
              </FormLabel>
              <FormControl variant="outlined" fullWidth>
                <SelectField
                  name="collaborationRole"
                  options={collaborationRoles}
                  value={formState.collaborationRole}
                  onChange={updateValues}
                  inputProps={{ "aria-label": "Collaboration Role" }}
                  data-testid="sd-collaboration-role-select"
                  renderOption={role => (
                    <MenuItem key={role} value={role}>
                      {t(`collaborationRole.${role}`)}
                    </MenuItem>
                  )}
                />
              </FormControl>
            </>
          )}
          {canExpireLinks(formState.access, formState.provider) && (
            <>
              <SwitchField
                color="primary"
                inputProps={{
                  "aria-label":
                    "Enable automatic expiration of Mail2Cloud links",
                  "data-testid": "sd-expiration-switch",
                }}
                label={t("form.fields.expiresInDays.label.switch")}
                description={t("form.fields.expiresInDays.description")}
                checked={enableExpiration}
                onChange={toggleEnableExpiration}
              />
              {enableExpiration && (
                <TextField
                  name="expiresInDays"
                  type="number"
                  label={t("form.fields.expiresInDays.label.input")}
                  variant="outlined"
                  value={formState.expiresInDays}
                  onChange={updateValues}
                  error={!!errors.expiresInDays}
                  helperText={t(errors.expiresInDays)}
                  onBlur={validateOnBlur}
                  inputProps={{ "data-testid": "sd-expiration-days-input" }}
                />
              )}
            </>
          )}
          {canPreventFileDownload(formState.access, formState.provider) && (
            <SwitchField
              inputProps={{
                "aria-label": "Prevent file downloads",
                "data-testid": "sd-prevent-download-switch",
              }}
              name="canDownload"
              label={t("form.fields.canDownload.label")} // Prevent file downloads
              description={t("form.fields.canDownload.description")}
              color="primary"
              checked={!formState.canDownload}
              onChange={toggleCanDownload}
            />
          )}

          <SwitchField
            name="removeDownloadLink"
            label={t("form.fields.removeDownloadLink.label")}
            description={t("form.fields.removeDownloadLink.description")}
            disabled={!formState.canDownload}
            color="primary"
            checked={formState.removeDownloadLink}
            onChange={updateValues}
            inputProps={{ "data-testid": "sd-remove-downloads-switch" }}
          />

          {canProtectWithPassword(formState.access, formState.provider) && (
            <SwitchField
              inputProps={{
                "aria-label": "Password Protected",
                "data-testid": "sd-password-switch",
              }}
              name="individualPassword"
              label={t("form.fields.individualPassword.label")}
              description={t("form.fields.individualPassword.description")}
              color="primary"
              checked={formState.individualPassword}
              onChange={updateValues}
            />
          )}
          <DialogActions>
            <Button
              color="primary"
              variant="outlined"
              disableElevation
              onClick={closeForm}
              data-testid="sd-form-cancel-btn"
            >
              {t("common:cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disableElevation
              data-testid="sd-form-submit-btn"
            >
              {!securityDefinition ? t("common:create") : t("common:save")}
            </Button>
          </DialogActions>
        </StyledForm>
      </DialogContent>
      {retentionConflict && (
        <RetentionConflictModal
          open
          origin="securityDefinition"
          details={retentionConflict}
          savingId={securityDefinition?.id}
          onClose={() => setRetentionConflict(null)}
        />
      )}
    </Dialog>
  );
}

function getAriaLabel(access: StorageAccessType) {
  switch (access) {
    case "OPEN":
      return "Accessible to anyone";
    case "COLLABORATORS":
      return "Accessible to the recipients of the email";
    case "ORGANIZATION":
      return "Accessible to people in the organization";
    case "NONE":
      return "Accessible to the folder collaborators";
  }
}
